import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { useAppStateContext } from 'src/AppState/AppState';
import { PromotedEventFormState } from './PromotedEventFormState';
import { Loader } from 'src/domains/layouts/shared/loader/Loader';
import { ErrorMessage } from 'src/domains/layouts/shared/formComponents/FormComponents.style';
import { FormComponent } from 'src/domains/layouts/shared/cms/form/Form.style';
import { FormHeader } from 'src/domains/layouts/shared/cms/form/formHeader/FormHeader';
import { ContentH5, ParagraphDefault } from 'src/domains/layouts/shared/typography/Content.style';
import { Popup } from 'src/domains/layouts/shared/popup/Popup';
import { SuccessPopup } from 'src/domains/layouts/shared/popup/popups/cms/SuccessPopup';
import { InputRow } from 'src/domains/layouts/shared/formComponents/input/Input.style';
import { Input } from 'src/domains/layouts/shared/formComponents/input/Input';
import { Button } from 'src/domains/layouts/shared/buttons/Button';
import { ButtonOnInputSide } from 'src/domains/layouts/shared/buttons/Button.style';
import {
    EventDataWrapper,
    PromotedEventsFormWrapper,
} from 'src/main/cms/PromotedEvents/promotedEventsForm/PromotedEvents.style';

type PropsType = {
    onCreate: () => void;
};

export const PromotedEventForm = observer('SportsForm', ({ onCreate }: PropsType) => {
    const {
        cmsState: { promotedEventsState },
        sdkStaff,
        trpcClient,
    } = useAppStateContext();
    const [promotedEventFormState] = useState(
        () => new PromotedEventFormState(trpcClient, promotedEventsState, sdkStaff.models)
    );

    const {
        eventId,
        sportName,
        competitionName,
        eventName,
        handleFindEvent,
        eventFoundData,
        isEventValid,
        handleCreateEvent,
        showCreatedPopup,
        handleHideCreatedPopup,
        isButtonDisabled,
        errorMessageForList,
    } = promotedEventFormState;
    return (
        <PromotedEventsFormWrapper>
            <FormHeader
                isEditMode={false}
                defaultLabel='Create promoted event'
            >
                Enter Event ID. If the event is from a sport or competition that don’t already have a category, it will
                automatically create one.
            </FormHeader>

            <FormComponent
                onSubmit={(e: React.FormEvent<HTMLFormElement>): void => {
                    e.preventDefault();
                }}
            >
                <Loader isActive={false} />
                <InputRow>
                    <Input
                        label='Event ID*'
                        isError={isEventValid === false}
                        isEmpty={eventId.value === ''}
                        formInputState={eventId}
                        dataTest='promoted-event-id-input'
                    />
                    <ButtonOnInputSide
                        size='regular'
                        version='secondary'
                        disabled={isEventValid === false}
                        onClick={(): Promise<void> => handleFindEvent()}
                        data-test='promoted-event-find-button'
                    >
                        Find Event
                    </ButtonOnInputSide>
                </InputRow>
                {eventFoundData === null ? null : (
                    <>
                        <EventDataWrapper>
                            <ContentH5>Sport:</ContentH5>
                            <ParagraphDefault data-test='promoted-event-found-sport'>{sportName}</ParagraphDefault>
                            <ContentH5>Competition:</ContentH5>
                            <ParagraphDefault data-test='promoted-event-found-competition'>
                                {competitionName}
                            </ParagraphDefault>
                            <ContentH5>Event:</ContentH5>
                            <ParagraphDefault data-test='promoted-event-found-event'>{eventName}</ParagraphDefault>
                        </EventDataWrapper>

                        <Button
                            isDisabled={isButtonDisabled}
                            onClick={async (): Promise<void> => {
                                await handleCreateEvent();
                                onCreate();
                            }}
                            dataTest='promoted-event-create-button'
                        >
                            Create Event
                        </Button>
                    </>
                )}
                {showCreatedPopup === false ? null : (
                    <Popup
                        width={384}
                        handleHidePopup={handleHideCreatedPopup}
                    >
                        <SuccessPopup
                            handleHidePopup={handleHideCreatedPopup}
                            itemLabel='promoted event'
                        />
                    </Popup>
                )}
                {errorMessageForList !== null && <ErrorMessage>{errorMessageForList}</ErrorMessage>}
            </FormComponent>
        </PromotedEventsFormWrapper>
    );
});
